import { PageHero } from "@/components/site/SiteLayout";
import { PRIVACY_META, PRIVACY_SECTIONS } from "@/data/privacyPolicy";
import { TERMS_META, TERMS_SECTIONS } from "@/data/termsOfService";

const Rich = ({ text }) => text.split(/(https:\/\/\S+|\S+@\S+\.\S+)/g).map((part, i) => {
  if (part.startsWith("https://")) return <a key={i} href={part} target={part.includes("clockaroo.com.au") ? undefined : "_blank"} rel="noreferrer" className="font-medium text-[#00A85C] underline-offset-2 hover:underline">{part}</a>;
  if (/\S+@\S+\.\S+/.test(part)) return <a key={i} href={`mailto:${part}`} className="font-medium text-[#00A85C] underline-offset-2 hover:underline">{part}</a>;
  return part;
});

const Block = ({ b }) => {
  if (b.type === "note") return <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs font-medium text-amber-900" data-testid="legal-draft-note">{b.text}</p>;
  if (b.type === "h3") return <h3 className="mt-6 text-base font-semibold text-[#0B2D5B]">{b.text}</h3>;
  if (b.type === "ul") return <ul className="ml-5 list-disc space-y-1.5 text-slate-600 marker:text-[#00A85C]">{b.items.map((i) => <li key={i}>{i}</li>)}</ul>;
  if (b.type === "dl") return (
    <dl className="divide-y divide-slate-100 rounded-2xl border border-slate-200">
      {b.items.map(([t, d]) => <div key={t} className="grid gap-1 p-4 sm:grid-cols-[200px_1fr]"><dt className="font-semibold text-[#0B2D5B]">{t}</dt><dd className="text-slate-600"><Rich text={d} /></dd></div>)}
    </dl>
  );
  return <p className="leading-relaxed text-slate-600"><Rich text={b.text} /></p>;
};

const DOCS = {
  privacy: {
    title: "Privacy Policy", sections: PRIVACY_SECTIONS, updated: PRIVACY_META.updated,
    rows: [["Effective date", PRIVACY_META.effective], ["Website", PRIVACY_META.website], ["Privacy contact", PRIVACY_META.contact]],
  },
  terms: { title: "Terms and Conditions of Service", sections: TERMS_SECTIONS, rows: TERMS_META.rows, updated: TERMS_META.updated },
};

const LegalDoc = ({ kind }) => {
  const doc = DOCS[kind];
  return (
  <div data-testid={`legal-page-${kind}`}>
    <PageHero eyebrow="Legal" title={doc.title}>
      <dl className="mt-6 flex flex-wrap gap-x-8 gap-y-2 text-sm text-slate-600" data-testid={`${kind}-meta`}>
        {doc.rows.map(([k, v]) => <div key={k}><dt className="inline font-semibold text-[#0B2D5B]">{k}: </dt><dd className="inline"><Rich text={v} /></dd></div>)}
      </dl>
    </PageHero>
    <section className="mx-auto grid max-w-7xl gap-12 px-4 py-14 sm:px-6 lg:grid-cols-12 lg:px-8">
      <nav className="lg:col-span-3" aria-label="Contents" data-testid={`${kind}-toc`}>
        <div className="lg:sticky lg:top-24">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E07800]">Contents</p>
          <ol className="mt-4 space-y-2 text-sm">
            {doc.sections.map((s) => <li key={s.id}><a href={`#${s.id}`} className="text-slate-600 transition-colors hover:text-[#00A85C]" data-testid={`${kind}-toc-${s.id}`}>{s.title}</a></li>)}
          </ol>
        </div>
      </nav>
      <article className="space-y-12 lg:col-span-9 lg:max-w-3xl">
        {doc.sections.map((s) => (
          <section key={s.id} id={s.id} className="scroll-mt-24 space-y-4" data-testid={`${kind}-section-${s.id}`}>
            <h2 className="text-2xl font-bold tracking-tight text-[#0B2D5B]">{s.title}</h2>
            {s.blocks.map((b, i) => <Block key={i} b={b} />)}
          </section>
        ))}
        <footer className="border-t border-slate-200 pt-6 text-sm text-slate-500" data-testid={`${kind}-footer`}>
          {doc.updated && <p>Last updated: {doc.updated}</p>}
          <p className="mt-1">© 2026 Clockaroo. All rights reserved.</p>
        </footer>
      </article>
    </section>
  </div>
  );
};

export default function Legal({ type }) {
  return <LegalDoc kind={type} />;
}
